import { Navigate, useParams } from "react-router";

import { useAuth } from "@/providers";
import { LoadingScreen, PageLayout } from "@/shared/components";
import { Callout } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";
import { featureFlagRoute } from "@/shared/routing/featureFlagRoute";

/**
 * Opens the Assets tab of Settings at the folder the rest of the URL names,
 * when the `og_` ID in the URL is the current organization's. The Assets tab
 * shows only the current organization's files, so for another organization
 * the user belongs to, this says whose files they are instead of opening the
 * current organization's folder of the same name.
 */
export function OrganizationAssetsRedirect() {
  const { orgId, "*": directoryPath } = useParams();
  const { currentOrganization } = useAuth();

  if (!currentOrganization) {
    return <LoadingScreen />;
  }

  if (orgId !== currentOrganization.org_id) {
    return (
      <PageLayout title="Roboto - Assets" headerContentLeft="Assets">
        <Callout variant="info">
          These files belong to another organization, {orgId}. Switch to that
          organization to see them in its Settings.
        </Callout>
      </PageLayout>
    );
  }

  return (
    <Navigate to={Goto.createOrganizationAssetsRoute(directoryPath)} replace />
  );
}

/**
 * `/organization-assets/:orgId/*`, where links to an organization's files land
 * (see `Goto.route.OrganizationAssetsById`). A top-level route, like the
 * device one, since Settings has no segment for the organization.
 */
export const OrganizationAssetsRedirectRoute = () =>
  featureFlagRoute({
    path: `${Goto.route.OrganizationAssetsById}/:orgId/*`,
    flagName: "release-asset-store",
    redirectTo: Goto.route.Settings.Organization,
    element: <OrganizationAssetsRedirect />,
  });
